import { Controller } from "@hotwired/stimulus"
import { setHidden } from "../helpers/hidden"

// The public floor plan's search and zone chips (docs/07 "Public floor plan").
//
// Filtering happens over a drawing and a table that are both already on the
// page. Four hundred stands is small, and a round trip per keystroke on a
// venue's wifi is exactly what somebody standing in the park does not need —
// the same call the exhibitors directory makes.
//
// Two things it must get right, because both are invisible when wrong: the
// map and the list have to agree (they are the same stands, and a person
// reading one must not see a different answer in the other), and a search
// that matches nothing has to SAY so — an empty drawing with no explanation
// reads as a broken page.
export default class extends Controller {
  static targets = ["search", "stand", "row", "count", "empty", "drawing", "bar", "group", "more",
    "sheet", "toggle", "note", "backdrop", "done"]
  static values = { closeSpan: Number }

  // Rows a zone's group shows before "Show all", when nothing is searched.
  static PREVIEW = 8

  connect() {
    this.zone = "all"
    // Paired by booth id, never by index: the drawing and the table are two
    // renderings of the same stands, and an off-by-one between them would
    // hide the wrong row while the map looked right.
    this.rows = new Map(this.rowTargets.map((row) => [row.dataset.boothId, row]))
    this.expanded = new Set()
    this.pin = this.pin.bind(this)
    window.addEventListener("scroll", this.pin, { passive: true })
    window.addEventListener("resize", this.pin)
    this.pin()
    this.filter()
    // ?q= (a stand link): zoom once map-viewport has connected.
    if (this.searchTarget.value.trim()) this.arrival = requestAnimationFrame(() => this.frameMatches())
  }

  disconnect() {
    cancelAnimationFrame(this.arrival)
    clearTimeout(this.searchTimer)
    this.stopGlide()
    window.removeEventListener("scroll", this.pin)
    window.removeEventListener("resize", this.pin)
  }

  // The bar is CSS sticky; data-stuck shows its ground once pinned. The nav's
  // height is measured: its padding changes at 640px.
  pin() {
    if (!this.hasBarTarget) return
    const nav = document.getElementById("topbar")
    const line = nav ? nav.offsetHeight : 0
    const stuck = this.element.getBoundingClientRect().top < line - 0.5
    if (line !== this.navLine) this.barTarget.style.setProperty("--nav-h", `${(this.navLine = line)}px`)
    this.barTarget.toggleAttribute("data-stuck", stuck)
  }

  choose(event) {
    const chip = event.currentTarget
    this.zone = chip.dataset.filter
    this.element.querySelectorAll("[data-filter]").forEach((button) => {
      const chosen = button === chip
      button.setAttribute("aria-pressed", String(chosen))
      // The design's chips are solid when on and ghosted when off.
      button.classList.toggle("ghost", !chosen)
    })
    // How many filters are on: a zone name here squeezed the search to nothing.
    if (this.hasNoteTarget) this.noteTarget.textContent = this.zone === "all" ? "" : " · 1"
    this.filter()
    this.frameZone()
  }

  // Typing filters at once and moves the map to the matches once the typing
  // pauses, so the map does not lurch per keystroke. Without this a match was
  // a ring on a speck at whole-venue zoom. Clearing goes back to the zone.
  search() {
    this.filter()
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.frameMatches(), 400)
  }

  frameMatches() {
    const shown = this.standTargets.filter((stand) => !stand.hasAttribute("hidden"))
    if (this.searchTarget.value.trim() === "" || shown.length === 0) return this.frameZone()

    const boxes = shown.map((stand) => stand.querySelector("rect")).map((rect) =>
      ["x", "y", "width", "height"].map((name) => Number(rect.getAttribute(name))))
    const left = Math.min(...boxes.map(([x]) => x))
    const top = Math.min(...boxes.map(([, y]) => y))
    const right = Math.max(...boxes.map(([x, , w]) => x + w))
    const bottom = Math.max(...boxes.map(([, y, , h]) => y + h))
    const width = Math.max((right - left) * 1.4, this.closeSpanValue)
    const height = Math.max((bottom - top) * 1.4, this.closeSpanValue * 0.75)
    this.frame([(left + right - width) / 2, (top + bottom - height) / 2, width, height].join(" "))
  }

  // The chosen zone's window: the whole venue, the whole zone, or on a phone
  // (where a whole zone is dots) a close-up of its busiest part.
  frameZone() {
    const chip = this.element.querySelector(`[data-filter="${this.zone}"]`)
    if (!chip || this.zone === "all") return this.frame(this.drawingTarget.dataset.fullViewBox)
    this.frame((this.phone() && chip.dataset.closeViewBox) || chip.dataset.viewBox)
  }

  // Every reframe glides (the user, 2026-10-20): zoom on a log scale, so it
  // reads as steady zooming rather than a lurch at one end, with the centre
  // travelling alongside. A drag or wheel on the map takes over at once.
  frame(viewBox) {
    const viewport = this.viewport()
    if (!viewport) return
    const to = viewport.fitted(viewport.parse(viewBox))
    const from = viewport.current()
    cancelAnimationFrame(this.glide)
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return this.land(viewport, to)

    const start = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - start) / 520)
      const e = t < 0.5 ? 4 * t ** 3 : 1 - ((2 - 2 * t) ** 3) / 2
      const width = from.width * (to.width / from.width) ** e
      const height = from.height * (to.height / from.height) ** e
      const x = from.x + from.width / 2 + (to.x + to.width / 2 - from.x - from.width / 2) * e
      const y = from.y + from.height / 2 + (to.y + to.height / 2 - from.y - from.height / 2) * e
      viewport.apply({ x: x - width / 2, y: y - height / 2, width, height })
      if (t < 1) this.glide = requestAnimationFrame(step)
      else this.land(viewport, to)
    }
    this.glide = requestAnimationFrame(step)
  }

  land(viewport, to) {
    viewport.apply(to)
    viewport.report()
  }

  stopGlide() { cancelAnimationFrame(this.glide) }

  viewport() { return this.application.getControllerForElementAndIdentifier(this.element, "map-viewport") }

  phone() { return window.matchMedia("(max-width: 640px)").matches }

  // The phone's bottom sheet of filters. Above 640px the same controls sit in
  // the bar and the Filters button is not shown.
  openSheet() {
    this.sheetTarget.toggleAttribute("data-open", true)
    this.sheetTarget.setAttribute("role", "dialog")
    this.sheetTarget.setAttribute("aria-modal", "true")
    this.backdropTarget.hidden = false
    this.toggleTarget.setAttribute("aria-expanded", "true")
    this.sheetTarget.querySelector("[aria-pressed=true]")?.focus()
  }

  // While the sheet is open Tab stays in it: it is modal, and the page behind
  // the backdrop is not where a keyboard user expects to land.
  trapFocus(event) {
    if (event.key !== "Tab" || !this.sheetTarget.hasAttribute("data-open")) return
    // Index, not .at(-1): the build targets Safari 15, which lacks Array#at.
    const stops = [...this.sheetTarget.querySelectorAll("button")]
    const first = stops[0]
    const last = stops[stops.length - 1]
    if (document.activeElement !== (event.shiftKey ? first : last)) return
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
  }

  closeSheet() {
    if (!this.sheetTarget.hasAttribute("data-open")) return
    this.sheetTarget.removeAttribute("data-open")
    this.sheetTarget.removeAttribute("role")
    this.sheetTarget.removeAttribute("aria-modal")
    this.backdropTarget.hidden = true
    this.toggleTarget.setAttribute("aria-expanded", "false")
    this.toggleTarget.focus()
  }

  // "Show all" in one zone's group of the list.
  expand({ currentTarget }) {
    this.expanded.add(currentTarget.closest("tbody").dataset.zoneKey)
    this.filter()
  }

  // Logos or names: the stylesheet shows the one data-labels names.
  label({ currentTarget: chip }) {
    this.drawingTarget.dataset.labels = chip.dataset.labels
    this.element.querySelectorAll("button[data-labels]").forEach((button) => {
      button.setAttribute("aria-pressed", button === chip)
      button.classList.toggle("ghost", button !== chip)
    })
  }

  filter() {
    const term = this.searchTarget.value.trim().toLowerCase()
    let shown = 0

    this.standTargets.forEach((stand) => {
      const visible = this.matches(stand, term)
      setHidden(stand, !visible)
      // A term that matches rings the stand on the map. Rings every match, not
      // just the first: "B-0" is three stands, and picking one of them to
      // highlight would be picking one at random.
      stand.classList.toggle("is-match", visible && term !== "")
      if (visible) shown += 1

      const row = this.rows.get(stand.dataset.boothId)
      if (row) setHidden(row, !visible)
    })

    this.trimGroups(term)

    this.countTarget.textContent = `Showing ${shown} of ${this.standTargets.length} stands`
    if (this.hasDoneTarget) this.doneTarget.textContent = `Show ${shown} ${shown === 1 ? "stand" : "stands"}`
    if (this.hasEmptyTarget) this.emptyTarget.hidden = shown > 0
  }

  // A zone's rows: hidden when none match, else cut to PREVIEW behind "Show
  // all" unless searched or opened. The map always shows every match.
  trimGroups(term) {
    this.groupTargets.forEach((group) => {
      const shown = [...group.querySelectorAll("[data-booth-id]")].filter((row) => !row.hasAttribute("hidden"))
      const open = term !== "" || this.expanded.has(group.dataset.zoneKey)
      shown.forEach((row, n) => { if (!open && n >= this.constructor.PREVIEW) setHidden(row, true) })
      setHidden(group, shown.length === 0)
      const more = this.moreTargets.find((target) => group.contains(target))
      if (more) setHidden(more, open || shown.length <= this.constructor.PREVIEW)
    })
  }

  matches(stand, term) {
    const matchesZone = this.zone === "all" || stand.dataset.zoneKey === this.zone
    const matchesTerm = term === "" || stand.dataset.search.includes(term)

    return matchesZone && matchesTerm
  }
}
